import { AlertColor, Card, Grid, Tooltip } from '@mui/material';
import { Box } from '@mui/system';
import React, { useContext, useEffect, useRef, useState } from 'react';
import MediumTypography from '../../components/formlib/MediumTypography';
import { ReactComponent as DownArrow } from '../../assets/images/downarrowcolored.svg';
import { ReactComponent as UpArrow } from '../../assets/images/uparrowcolored.svg';
import Textinput from '../../components/formlib/Textinput';
import DurationPicker from '../../components/formlib/DurationPicker';
import DatePickerComponent from '../../components/formlib/DatePickerComponent';
import AutocompleteSearch from '../../components/formlib/AutocompleteSearch';
import { Formik, FormikErrors, FormikProps } from 'formik';
import * as yup from 'yup';
import dayjs, { Dayjs } from 'dayjs';
import ButtonComponent from '../../components/formlib/ButtonComponent';
import {
  createWaiver,
  getAllWaivers,
  updateWaiver,
} from '../../services/waiversApi/WaiversApis';
import { LookUpContext } from '../../context/LookUpContextProvider';
import { OptionType, WaiversType } from '../../utils/type';
import MultipleAutocompleteSearch from '../../components/formlib/MultipleAutocompleteSearch';
import EmptyScreen from '../../components/shared/EmptyScreen';
import { checkPermissionForFeature } from '../../utils/checkPermission';
import { LoaderContext, LoaderContextType } from '../../layouts/AppSidebar';
import {
  isCustomError,
  ApiError,
  ApiMessage,
} from '../../services/ApiResponseHandler';
import { formatNumberToDecimal } from '../../utils/utilities';
import {
  formatToDurationFromDecimal,
  formatToDecimalFromDuration,
} from '../../utils/dateUtil';
import SnackBarComponent from '../../components/formlib/SnackBarComponent';
import { Context } from '../../LanguageWrapper';
import _ from 'lodash';
import ModalPopup from '../../components/formlib/ModalPopup';
import { UnSavedChangesContext } from '../../context/UnSavedChangesProvider';
import CheckBoxComponent from '../../components/formlib/CheckBoxComponent';
import { useMultiplePermissions } from '../../hooks/usePermissions';
import { REFERRAL_STATUS_KEY } from '../../services/Constant';

const emptyWaiver: WaiversType = {
  waiverIndex: 0,
  waiverNumber: '',
  startDate: null,
  endDate: null,
  services: [],
  coTx: false,
  countType: '',
  hoursStartingBalanceDec: null,
  hoursUsedBalanceDec: null,
  hoursRemainingBalanceDec: null,
  serviceStartingBalance: null,
  serviceUsedBalance: null,
  serviceRemainingBalance: null,
  status: 'Pending',
};

interface WaiverValuesWatcherProps {
  values: WaiverFormValues[];
  setInitialWaiver: React.Dispatch<
    React.SetStateAction<WaiverFormValues[] | null>
  >;
}

const WaiverValuesWatcher = ({
  values,
  setInitialWaiver,
}: WaiverValuesWatcherProps) => {
  useEffect(() => {
    setInitialWaiver(values);
  }, [values]);

  return null;
};
type WaiverFormValues = WaiversType & {
  hoursStartingBalanceDecDisplay: string | null;
};

const toWaiversType = (formValue: WaiverFormValues): WaiversType => {
  const { hoursStartingBalanceDecDisplay, ...rest } = formValue;
  return rest as WaiversType;
};

const WaiversScreen = () => {
  const [openAccordian, setOpenAccordian] = useState<boolean>(true);
  const [openAccordianIndex, setOpenAccordianIndex] = useState<number>(0);
  const [initialWaiver, setInitialWaiver] = useState<WaiverFormValues[] | null>(
    null,
  );
  const [initialBackupWaiver, setInitialBackupWaiver] = useState<
    WaiverFormValues[] | null
  >(null);
  const [services, setServices] = useState<OptionType[]>([]);
  const [waiversType, setWaiversType] = useState<OptionType[]>([]);
  const [showEmptyScreen, setShowEmptyScreen] = useState<boolean>(false);
  const { toggleLoader } = useContext(LoaderContext) as LoaderContextType;
  const [open, setOpen] = useState(false);
  const [toastrVariable, setToastrVariable] =
    React.useState<AlertColor>('info');
  const [toastrDefaultMessage, setToastrDefaultMessage] = useState('');
  const [showUnsavedAlertDialog, setShowUnsavedAlertDialog] =
    useState<boolean>(false);
  const [toastrId, setToastrId] = React.useState('');
  const formRef = useRef<FormikProps<WaiverFormValues[]>>(null);
  const { lookups } = useContext(LookUpContext);
  const { programs } = useContext(Context);
  const { handleUpdateUnsavedChanges } = useContext(UnSavedChangesContext);
  const parentRef = useRef<HTMLDivElement>(null);
  const clientId = localStorage.getItem('ClientId');

  const getWaivers = (cId: string) => {
    getAllWaivers(cId)
      .then((res) => {
        if (res.length > 0) {
          toggleLoader(false);

          // Check if any waiver is active
          const isAnyActiveWaiver = res.some((e) => e.status === 'Active');

          if (isAnyActiveWaiver) {
            // Find the index of the first active waiver
            const activeIndex = res.findIndex((e) => e.status === 'Active');
            setOpenAccordianIndex(activeIndex); // Set the active waiver index
            setOpenAccordian(true); // Open the accordion
          } else {
            // If no active waiver, find the matching waiver number
            const openIndex = res.findIndex(
              (e) =>
                e.waiverNumber ===
                formRef.current?.values[openAccordianIndex]?.waiverNumber,
            );

            if (openIndex !== -1) {
              setOpenAccordianIndex(openIndex); // Set the found waiver index
              setOpenAccordian(true); // Open the accordion
            }
          }

          const waiverFormValues = res.map((e, index) => ({
            ...e,
            waiverIndex: index,
            hoursStartingBalanceDecDisplay: formatNumberToDecimal(
              e.hoursStartingBalanceDec,
            ),
          }));

          formRef.current?.setValues(waiverFormValues);
          setInitialWaiver(waiverFormValues);
          setInitialBackupWaiver(waiverFormValues);
        } else {
          toggleLoader(false);
          setShowEmptyScreen(true);
        }
      })
      .catch((error) => {
        setOpen(true);
        toggleLoader(false);
        if (isCustomError(error)) {
          const apiError = error as ApiError;
          setToastrId(apiError.id);
          setToastrDefaultMessage(apiError.message);
        } else {
          setToastrVariable('error');
          setToastrId('Waivers.errorMessage');
          setToastrDefaultMessage('Failed to get Waiver data');
        }
      });
  };
  useEffect(() => {
    if (clientId) {
      toggleLoader(true);
      getWaivers(clientId);
    }
  }, []);
  useEffect(() => {
    if (lookups === null) {
      return;
    }

    const selectedServices =
      formRef.current?.values[openAccordianIndex]?.services ?? [];

    setServices(
      lookups.serviceCode
        .filter((e) => e.active || selectedServices.includes(e.code))
        .map((e) => ({
          label: `${e.code} - ${e.description}`,
          id: e.code,
        })),
    );

    setWaiversType(() => {
      return lookups.waiverCountType.map((e) => ({
        label: e.description,
        id: e.code,
      }));
    });
  }, [
    lookups,
    openAccordianIndex,
    formRef.current?.values[openAccordianIndex]?.services,
  ]);

  useEffect(() => {
    handleUpdateUnsavedChanges(!_.isEqual(initialBackupWaiver, initialWaiver));
  }, [initialWaiver, initialBackupWaiver]);

  const permissions = useMultiplePermissions([
    'backend.clients',
    'backend.in_active_referrals',
    'backend.waiver_information',
  ]);
  const referralStatus = localStorage.getItem(REFERRAL_STATUS_KEY) ?? 'None';
  const hasEditPermission =
    referralStatus === 'Inactive'
      ? permissions['backend.clients'].canEdit &&
        permissions['backend.in_active_referrals'].canEdit &&
        permissions['backend.waiver_information'].canEdit
      : (permissions['backend.clients'].canEdit ||
          permissions['backend.in_active_referrals'].canEdit) &&
        permissions['backend.waiver_information'].canEdit;

  useEffect(() => {
    if (!hasEditPermission) {
      const handleKeyDown = (e: KeyboardEvent) => {
        if (e.key === 'Tab') {
          e.preventDefault();
        }
      };
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [hasEditPermission]);

  const validationSchema = yup.array().of(
    yup.object().shape({
      waiverIndex: yup.number(),
      waiverNumber: yup.string().when('waiverIndex', {
        is: (waiverIndex: number) => waiverIndex === openAccordianIndex,
        then: yup.string().required('Waivers.waiverNumberIsRequired'),
        otherwise: yup.string().notRequired(),
      }),
      startDate: yup.date().when('waiverIndex', {
        is: (waiverIndex: number) => waiverIndex === openAccordianIndex,
        then: yup
          .date()
          .required('Waivers.waiverStartDateIsRequired')
          .test({
            name: 'startDate',
            test: function (value, context) {
              const startDate = context.parent?.startDate;

              if (value && startDate) {
                const pickerMin = '01/01/1900';
                const pickerMax = '12/31/2099';

                if (
                  dayjs(value).isBefore(pickerMin) ||
                  dayjs(value).isAfter(pickerMax)
                ) {
                  return context.createError({
                    message: 'datePickerMinDateMaxDateValidate',
                  });
                }
              }

              return true;
            },
          }),
        otherwise: yup.date().notRequired().nullable(),
      }),
      endDate: yup.date().when('waiverIndex', {
        is: (waiverIndex: number) => waiverIndex === openAccordianIndex,
        then: yup
          .date()
          .required('Waivers.waiverEndDateIsRequired')
          .test({
            name: 'endDate',
            message: 'Waivers.waiverEndDateShouldBeGreaterThanStartDate',
            test: function (value, context) {
              const startDate = context.parent?.startDate;

              if (value && startDate) {
                const isBefore = dayjs(value).isBefore(
                  dayjs(startDate as Date),
                );

                if (isBefore) {
                  return context.createError({
                    message:
                      'Waivers.waiverEndDateShouldBeGreaterThanStartDate',
                  });
                }
              }

              const pickerMin = '01/01/1900';
              const pickerMax = '12/31/2099';

              if (
                value &&
                (dayjs(value).isBefore(pickerMin) ||
                  dayjs(value).isAfter(pickerMax))
              ) {
                return context.createError({
                  message: 'datePickerMinDateMaxDateValidate',
                });
              }

              return true;
            },
          }),
        otherwise: yup.date().notRequired().nullable(),
      }),
      services: yup
        .array()
        .of(yup.string())
        .when('waiverIndex', {
          is: (waiverIndex: number) => waiverIndex === openAccordianIndex,
          then: yup
            .array()
            .of(yup.string().required('Waivers.waiverServiceIsRequired'))
            .min(1, 'Waivers.waiverAtLeastOneServiceRequired'),
          otherwise: yup.array().notRequired(),
        }),
      countType: yup.string().when('waiverIndex', {
        is: (waiverIndex: number) => waiverIndex === openAccordianIndex,
        then: yup.string().required('Waivers.waiverCountTypeIsRequired'),
        otherwise: yup.string().notRequired(),
      }),
      hoursStartingBalanceDec: yup.number().when(['waiverIndex', 'countType'], {
        is: (waiverIndex: number, countType: string) =>
          waiverIndex === openAccordianIndex && countType === 'H',

        then: yup
          .number()
          .required('Waivers.waiverStartingBalanceIsRequired')
          .test(
            'not-zero',
            'Waivers.waiverStartingBalanceCannotbe00:00',
            (value) => value !== undefined && value !== null && value !== 0,
          )
          .test(
            'valid-hours-starting-balance',
            'Waivers.waiverStartingBalanceInvalid',
            (value) => {
              if (value === undefined || value === null) return false;
              if (value < 0 || value > 99.75) return false;

              const decimalPart = Math.round((value % 1) * 100);
              return [0, 25, 50, 75].includes(decimalPart);
            },
          ),

        otherwise: yup.number().notRequired().nullable(),
      }),
      hoursStartingBalanceDecDisplay: yup.string().nullable(),

      serviceStartingBalance: yup.number().when(['waiverIndex', 'countType'], {
        is: (waiverIndex: number, countType: string) =>
          waiverIndex === openAccordianIndex && countType === 'V',
        then: yup
          .number()
          .required('Waivers.waiverserviceStartingBalanceIsRequired')
          .min(1, 'Waivers.waiverServiceStartingRangeValidation')
          .max(99, 'Waivers.waiverServiceStartingRangeValidation'),
        otherwise: yup.number().notRequired().nullable(),
      }),
    }),
  );

  const handleAddNewWaiver = () => {
    if (initialWaiver) {
      setOpenAccordianIndex(initialWaiver.length);
      setOpenAccordian(true);
    }
    const selectedProgram = localStorage.getItem('selectedProgram');

    if (selectedProgram) {
      const selectedProgramId = JSON.parse(selectedProgram).id;
      setInitialWaiver((prev) => {
        return [
          ...(prev || []),
          {
            ...emptyWaiver,
            waiverIndex: prev?.length,
            programId: selectedProgramId ?? '',
            hoursStartingBalanceDecDisplay: formatNumberToDecimal(
              emptyWaiver.hoursStartingBalanceDec,
            ),
          },
        ];
      });
      setInitialBackupWaiver((prev) => {
        return [
          ...(prev || []),
          {
            ...emptyWaiver,
            waiverIndex: prev?.length,
            programId: selectedProgramId ?? '',
            hoursStartingBalanceDecDisplay: formatNumberToDecimal(
              emptyWaiver.hoursStartingBalanceDec,
            ),
          },
        ];
      });
    }
  };

  const handleCreateWaiver = (props: WaiversType) => {
    if (clientId) {
      createWaiver(props, clientId)
        .then((res: ApiMessage) => {
          getWaivers(clientId);
          setOpen(true);
          setToastrVariable('success');
          setToastrDefaultMessage(res.message);
          setToastrId('Waiver.Add.successmsg');
        })
        .catch((error) => {
          setOpen(true);
          toggleLoader(false);
          setToastrVariable('error');
          if (isCustomError(error)) {
            const apiError = error as ApiError;
            setToastrId(apiError.id);
            setToastrDefaultMessage(apiError.message);
          } else {
            setToastrId('Waiver.Add.errormsg');
            setToastrDefaultMessage(error.message);
          }
        });
    }
  };

  const handleUpdateWaiver = (props: WaiversType) => {
    if (clientId) {
      updateWaiver(props, clientId, props.id ? props.id : '')
        .then((res: ApiMessage) => {
          getWaivers(clientId);
          setOpen(true);
          setToastrVariable('success');
          setToastrDefaultMessage(res.message);
          setToastrId('Waiver.Add.successmsg');
        })
        .catch((error) => {
          toggleLoader(false);
          setOpen(true);
          setToastrVariable('error');
          if (isCustomError(error)) {
            const apiError = error as ApiError;
            setToastrId(apiError.id);
            setToastrDefaultMessage(apiError.message);
          } else {
            const response = error as ApiMessage;
            setToastrId('Waiver.Add.errormsg');
            setToastrDefaultMessage(response.message);
          }
        });
    }
  };

  const handleClose = (
    event?: React.SyntheticEvent | Event,
    reason?: string,
  ) => {
    if (reason === 'clickaway') {
      return;
    }
    setOpen(false);
  };

  return (
    <Box>
      <ModalPopup
        open={showUnsavedAlertDialog}
        onCancel={() => {
          setShowUnsavedAlertDialog(false);
        }}
        description="formUnsavedChangesMessage"
        onOk={() => {
          if (clientId) {
            toggleLoader(true);
            handleUpdateUnsavedChanges(false);
            getWaivers(clientId);
            setShowUnsavedAlertDialog(false);
          }
        }}
        labelId1="Clientpage.cancelbtn"
        negativeActionLabel="cancelText"
        labelId2="Clientpage.Okbtn"
        positiveActionLabel="Ok"
      />
      {showEmptyScreen ? (
        <EmptyScreen
          titleLabelId="Waivers.emptyScreenTitle"
          defaultTitleText="No Waivers added yet."
          buttonLabelId="Waivers.emptyScreenButton"
          defaultButtonTitleText="Add New Waiver"
          showButton={
            checkPermissionForFeature('backend.clients', 'editPermission') &&
            checkPermissionForFeature(
              'backend.waiver_information',
              'addPermission',
            )
          }
          onButtonClick={() => {
            const selectedProgram = localStorage.getItem('selectedProgram');
            if (selectedProgram) {
              const selectedProgramId = JSON.parse(selectedProgram).id;
              setShowEmptyScreen(false);
              setInitialWaiver([
                {
                  ...emptyWaiver,
                  waiverIndex: 0,
                  programId: selectedProgramId ?? '',
                  hoursStartingBalanceDecDisplay: formatNumberToDecimal(
                    emptyWaiver.hoursStartingBalanceDec,
                  ),
                },
              ]);
              setInitialBackupWaiver([
                {
                  ...emptyWaiver,
                  waiverIndex: 0,
                  programId: selectedProgramId ?? '',
                  hoursStartingBalanceDecDisplay: formatNumberToDecimal(
                    emptyWaiver.hoursStartingBalanceDec,
                  ),
                },
              ]);
            }
          }}
        />
      ) : (
        <Card className="formCardview">
          <SnackBarComponent
            open={open}
            handleClose={handleClose}
            successOrError={toastrVariable}
            labelId={toastrId}
            defaultMessageId={toastrDefaultMessage}
          />
          {initialWaiver?.map((item, sectionIndex) => (
            <Box
              key={item.id}
              className={
                sectionIndex === initialWaiver.length - 1
                  ? 'borderRadius4 p-lg'
                  : 'borderRadius4 p-lg mb-md'
              }
              sx={{ backgroundColor: '#ECF9F8', border: '1px solid #00C6B8' }}
            >
              <Box
                className="flex__ align__items__center justifyContentSpaceBtw"
                sx={{
                  marginBottom:
                    openAccordian && sectionIndex === openAccordianIndex
                      ? '20px'
                      : '0px',
                }}
              >
                <Box className="flex__" sx={{ width: '100%' }}>
                  <MediumTypography
                    labelid="Waivers.programName"
                    defaultlabel="Program Name:"
                    fontSize="16px"
                    textColor="#97A6A5"
                    className="mr-xs"
                  />
                  <Tooltip
                    title={
                      programs.find(
                        (programName) => programName.id === item.programId,
                      )?.label || '-'
                    }
                    arrow
                    followCursor
                    className="minWidthContent"
                  >
                    <Box>
                      <MediumTypography
                        sxProps={{
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          cursor: 'pointer',
                          textOverflow: 'ellipsis',
                          maxWidth: '150px',
                          fontWeight: 500,
                        }}
                        label={
                          programs.find(
                            (programName) => programName.id === item.programId,
                          )?.label || '-'
                        }
                        fontSize="16px"
                        textColor="#2A4241"
                      />
                    </Box>
                  </Tooltip>
                </Box>
                {openAccordian && sectionIndex === openAccordianIndex ? null : (
                  <Box
                    className="flex__ justifyContent-Center"
                    sx={{ width: '100%' }}
                  >
                    <MediumTypography
                      labelid="Waivers.waiversNumber"
                      defaultlabel="Waiver Number:"
                      fontSize="16px"
                      textColor="#97A6A5"
                      className="mr-xs"
                    />
                    <Box sx={{ width: '120px' }}>
                      <MediumTypography
                        label={item.waiverNumber || '-'}
                        fontSize="16px"
                        textColor="#2A4241"
                      />
                    </Box>
                  </Box>
                )}
                <Box
                  className="flex__ align__items__center justifyContent-End"
                  sx={{ width: '100%' }}
                >
                  <MediumTypography
                    labelid="Waivers.status"
                    defaultlabel="Status:"
                    fontSize="16px"
                    textColor="#97A6A5"
                    className="mr-xs"
                  />
                  <Box sx={{ width: '100px' }}>
                    <MediumTypography
                      label={item.status}
                      fontSize="16px"
                      textColor={
                        item.status === 'Active'
                          ? '#37D183'
                          : item.status === 'Not Started'
                          ? '#F9B803'
                          : item.status === 'Inactive'
                          ? '#EB4C60'
                          : '#F9B803'
                      }
                    />
                  </Box>
                  {openAccordian && sectionIndex === openAccordianIndex ? (
                    <UpArrow
                      className="cursorPointer ml-sm"
                      onClick={() => {
                        setOpenAccordianIndex((prev) => prev);
                        setOpenAccordian(false);
                      }}
                    />
                  ) : (
                    <DownArrow
                      className="cursorPointer ml-sm"
                      onClick={() => {
                        setOpenAccordianIndex(sectionIndex);
                        setOpenAccordian(true);
                      }}
                    />
                  )}
                </Box>
              </Box>
              {openAccordian &&
                sectionIndex === openAccordianIndex &&
                initialWaiver && (
                  <Formik
                    initialValues={initialWaiver}
                    validationSchema={validationSchema}
                    innerRef={formRef}
                    onSubmit={(values) => {
                      toggleLoader(true);
                      const request = structuredClone(
                        values,
                      ) as WaiverFormValues[];
                      const current = toWaiversType(
                        request[openAccordianIndex],
                      );
                      if (current.countType === 'V') {
                        current.hoursStartingBalanceDec = 0;
                        current.hoursUsedBalanceDec = 0;
                        current.hoursRemainingBalanceDec = 0;
                      } else if (current.countType === 'H') {
                        const starting = current.hoursStartingBalanceDec ?? 0;
                        const used = current.hoursUsedBalanceDec ?? 0;
                        current.hoursUsedBalanceDec = used;
                        current.hoursRemainingBalanceDec = starting - used;
                        current.serviceStartingBalance = 0;
                        current.serviceRemainingBalance = 0;
                        current.serviceUsedBalance = 0;
                      }
                      if (current.id) {
                        handleUpdateWaiver(current);
                      } else {
                        handleCreateWaiver(current);
                      }
                    }}
                  >
                    {({
                      values,
                      errors,
                      touched,
                      setFieldValue,
                      handleSubmit,
                    }) => {
                      return (
                        <Box
                          ref={parentRef}
                          style={{
                            pointerEvents: hasEditPermission ? 'auto' : 'none',
                            opacity: hasEditPermission ? 'unset' : '0.8',
                          }}
                        >
                          <WaiverValuesWatcher
                            values={values}
                            setInitialWaiver={setInitialWaiver}
                          />
                          <Grid
                            container
                            xs={12}
                            rowSpacing={'24px'}
                            columnSpacing={'50px'}
                          >
                            <Grid item xs={5}>
                              <Textinput
                                className="bg__white"
                                labelid="Waivers.enterWaiversNumber"
                                defaultlabelid="Waiver Number"
                                Value={values[openAccordianIndex].waiverNumber}
                                handlechange={(e) => {
                                  setFieldValue(
                                    `${openAccordianIndex}.waiverNumber`,
                                    e,
                                  );
                                }}
                                inputProps={{
                                  maxLength: 10,
                                }}
                                Required
                              />
                              {errors[openAccordianIndex] &&
                                (
                                  errors[
                                    openAccordianIndex
                                  ] as FormikErrors<WaiverFormValues>
                                ).waiverNumber &&
                                touched[openAccordianIndex] &&
                                (
                                  touched[
                                    openAccordianIndex
                                  ] as FormikErrors<WaiverFormValues>
                                ).waiverNumber && (
                                  <MediumTypography
                                    labelid={
                                      (
                                        touched[
                                          openAccordianIndex
                                        ] as FormikErrors<WaiverFormValues>
                                      ).waiverNumber
                                    }
                                    defaultlabel="Waiver Number is Required"
                                    sxProps={{
                                      paddingTop: '8px',
                                      color: 'var(--thom-radical-red)',
                                    }}
                                  />
                                )}
                            </Grid>
                            <Grid item xs={7} />
                            <Grid item xs={5}>
                              <DatePickerComponent
                                className="bg__white"
                                inputFormat="MM/DD/YYYY"
                                labelid={'Waivers.waiverbegindate'}
                                defaultlabelid={'Waiver Begin Date'}
                                value={dayjs(
                                  values[openAccordianIndex].startDate,
                                )}
                                handlechange={(e: Dayjs | null) => {
                                  const formattedDate =
                                    dayjs(e).format('MM/DD/YYYY');
                                  if (e === null) {
                                    setFieldValue(
                                      `${openAccordianIndex}.startDate`,
                                      '',
                                    );
                                  } else {
                                    setFieldValue(
                                      `${openAccordianIndex}.startDate`,
                                      formattedDate,
                                    );
                                  }
                                }}
                                required
                              />
                              {errors[openAccordianIndex]?.startDate &&
                                touched[openAccordianIndex]?.startDate && (
                                  <MediumTypography
                                    labelid={
                                      errors[openAccordianIndex]?.startDate
                                    }
                                    defaultlabel="Waiver Begin Date is Required"
                                    sxProps={{
                                      paddingTop: '8px',
                                      color: 'var( --thom-radical-red)',
                                    }}
                                  />
                                )}
                            </Grid>
                            <Grid item xs={5}>
                              <DatePickerComponent
                                className="bg__white"
                                labelid={'Waivers.waiverenddate'}
                                inputFormat="MM/DD/YYYY"
                                defaultlabelid={'Waiver End Date'}
                                minDate={dayjs(
                                  values[openAccordianIndex].startDate,
                                )}
                                value={dayjs(
                                  values[openAccordianIndex].endDate,
                                )}
                                handlechange={(e: Dayjs | null) => {
                                  const formattedDate =
                                    dayjs(e).format('MM/DD/YYYY');
                                  if (e === null) {
                                    setFieldValue(
                                      `${openAccordianIndex}.endDate`,
                                      '',
                                    );
                                  } else {
                                    setFieldValue(
                                      `${openAccordianIndex}.endDate`,
                                      formattedDate,
                                    );
                                  }
                                }}
                                required
                              />
                              {errors[openAccordianIndex]?.endDate &&
                                touched[openAccordianIndex]?.endDate && (
                                  <MediumTypography
                                    labelid={
                                      errors[openAccordianIndex]?.endDate
                                    }
                                    defaultlabel="Waiver End Date is Required"
                                    sxProps={{
                                      paddingTop: '8px',
                                      color: 'var( --thom-radical-red)',
                                    }}
                                  />
                                )}
                            </Grid>
                            <Grid item xs={6}>
                              <MediumTypography
                                labelid="Waivers.limitServices"
                                defaultlabel="Limit Services"
                              />
                            </Grid>
                            <Grid item xs={6} />
                            <Grid item xs={5}>
                              <MultipleAutocompleteSearch
                                sxProps={{
                                  backgroundColor: 'white',
                                }}
                                labelId={'Waivers.selectSpecificService'}
                                defaultId={'Specific Services'}
                                value={values[openAccordianIndex].services.map(
                                  (service) => {
                                    return {
                                      id: service,
                                      label:
                                        services.find(
                                          (servicee) => servicee.id === service,
                                        )?.label || '',
                                    };
                                  },
                                )}
                                data={services}
                                onSelectItem={(e) => {
                                  setFieldValue(
                                    `${openAccordianIndex}.services`,
                                    e.map((itemm) => itemm.id),
                                  );
                                }}
                                disableLabel={false}
                                optionHintId={'No contacts found'}
                                required
                              />
                              {errors[openAccordianIndex]?.services &&
                                touched[openAccordianIndex]?.services && (
                                  <MediumTypography
                                    labelid={
                                      errors[openAccordianIndex]
                                        ?.services as string
                                    }
                                    defaultlabel="Waiver Services is Required"
                                    sxProps={{
                                      paddingTop: '8px',
                                      color: 'var( --thom-radical-red)',
                                    }}
                                  />
                                )}
                            </Grid>
                            <Grid
                              item
                              xs={4}
                              className="flex__ align__items__center"
                            >
                              <CheckBoxComponent
                                ischecked={values[openAccordianIndex].coTx}
                                CheckHandleChange={(e) => {
                                  setFieldValue(
                                    `${openAccordianIndex}.coTx`,
                                    e.target.checked,
                                  );
                                }}
                                labelid="Waivers.coTreatment"
                                defaultlabelid="Co-Treatment"
                              />
                            </Grid>
                            <Grid xs={8} />
                            {/* <Grid item xs={6}>
                              <MediumTypography
                                labelid="Waivers.doCount"
                                defaultlabel="Do Count"
                              />
                            </Grid>
                            <Grid item xs={6} /> */}
                            <Grid item xs={5}>
                              <AutocompleteSearch
                                className="bg__white"
                                labelId={'Waivers.selectWaiverCountType'}
                                defaultId={'Select Waiver Count Type'}
                                selectedValue={{
                                  id:
                                    waiversType.find(
                                      (waiverType) =>
                                        waiverType.id ===
                                        values[openAccordianIndex].countType,
                                    )?.id || '',
                                  label:
                                    waiversType.find(
                                      (waiverType) =>
                                        waiverType.id ===
                                        values[openAccordianIndex].countType,
                                    )?.label || '',
                                }}
                                data={waiversType}
                                onSelectItem={(e) => {
                                  if (e?.id) {
                                    setFieldValue(
                                      `${openAccordianIndex}.countType`,
                                      e?.id,
                                    );
                                  } else {
                                    setFieldValue(
                                      `${openAccordianIndex}.countType`,
                                      '',
                                    );
                                  }
                                }}
                                disableLabel={false}
                                Required
                              />
                              {errors[openAccordianIndex]?.countType &&
                                touched[openAccordianIndex]?.countType && (
                                  <MediumTypography
                                    labelid={
                                      errors[openAccordianIndex]?.countType
                                    }
                                    defaultlabel="Count Type is Required"
                                    sxProps={{
                                      paddingTop: '8px',
                                      color: 'var( --thom-radical-red)',
                                    }}
                                  />
                                )}
                            </Grid>
                            {values[openAccordianIndex].countType === 'H' && (
                              <>
                                <Grid item xs={5}>
                                  <DurationPicker
                                    uniqueId={`${openAccordianIndex}.hoursStartingBalance`}
                                    backgroundColor="#ffffff"
                                    labelId={'Waivers.startingWaiverBalance'}
                                    defaultLabel={'Starting Waiver Balance'}
                                    interval={25}
                                    maxDuration={{ hour: 99, minutes: 45 }}
                                    duration={
                                      values[openAccordianIndex]
                                        ?.hoursStartingBalanceDec !== null
                                        ? formatToDurationFromDecimal(
                                            values[openAccordianIndex]
                                              .hoursStartingBalanceDec ?? 0,
                                          )
                                        : { hour: 0, minutes: 0 }
                                    }
                                    required={true}
                                    setDuration={(duration) => {
                                      if (
                                        duration.hour === 0 &&
                                        duration.minutes === 0
                                      ) {
                                        setFieldValue(
                                          `${openAccordianIndex}.hoursStartingBalanceDec`,
                                          null,
                                        );
                                        setFieldValue(
                                          `${openAccordianIndex}.hoursStartingBalanceDecDisplay`,
                                          '',
                                        );
                                      } else {
                                        const decimalValue =
                                          formatToDecimalFromDuration(duration);
                                        setFieldValue(
                                          `${openAccordianIndex}.hoursStartingBalanceDec`,
                                          decimalValue,
                                        );
                                        setFieldValue(
                                          `${openAccordianIndex}.hoursStartingBalanceDecDisplay`,
                                          decimalValue.toString(),
                                        );
                                      }
                                    }}
                                  />
                                  {errors[openAccordianIndex]
                                    ?.hoursStartingBalanceDec &&
                                    touched[openAccordianIndex]
                                      ?.hoursStartingBalanceDec && (
                                      <MediumTypography
                                        labelid={
                                          errors[openAccordianIndex]
                                            ?.hoursStartingBalanceDec
                                        }
                                        defaultlabel="Sarting Balance cannot be empty"
                                        sxProps={{
                                          paddingTop: '8px',
                                          color: 'var( --thom-radical-red)',
                                        }}
                                      />
                                    )}
                                </Grid>
                                <Grid item xs={2}></Grid>
                                <Grid item xs={5}>
                                  <DurationPicker
                                    uniqueId={`${openAccordianIndex}.hoursUsedBalance`}
                                    backgroundColor="#ffffff"
                                    labelId={'Waivers.waiverCountsUsed'}
                                    defaultLabel={'Waiver Counts Used'}
                                    interval={25}
                                    maxDuration={{ hour: 99, minutes: 45 }}
                                    duration={
                                      values[openAccordianIndex]
                                        ?.hoursUsedBalanceDec !== null
                                        ? formatToDurationFromDecimal(
                                            values[openAccordianIndex]
                                              .hoursUsedBalanceDec ?? 0,
                                          )
                                        : { hour: 0, minutes: 0 }
                                    }
                                    required={false}
                                    setDuration={() => {}}
                                    disabled={true}
                                  />
                                </Grid>
                                <Grid item xs={5}>
                                  <DurationPicker
                                    uniqueId={`${openAccordianIndex}.hoursRemainingBalance`}
                                    backgroundColor="#ffffff"
                                    labelId={'Waivers.waiverCountsBalance'}
                                    defaultLabel={'Waiver Counts Balance'}
                                    interval={25}
                                    maxDuration={{ hour: 99, minutes: 45 }}
                                    duration={
                                      values[openAccordianIndex]
                                        ?.hoursRemainingBalanceDec !== null
                                        ? formatToDurationFromDecimal(
                                            values[openAccordianIndex]
                                              .hoursRemainingBalanceDec ?? 0,
                                          )
                                        : { hour: 0, minutes: 0 }
                                    }
                                    required={false}
                                    setDuration={() => {}}
                                    disabled={true}
                                  />
                                </Grid>
                                <Grid item xs={2} />
                              </>
                            )}
                            {values[openAccordianIndex].countType === 'V' && (
                              <>
                                <Grid item xs={5}>
                                  <Textinput
                                    className="bg__white"
                                    labelid="Waivers.serviceStartingBalance"
                                    defaultlabelid="Service Starting Balance"
                                    // type="number"
                                    Value={
                                      values[openAccordianIndex]
                                        .serviceStartingBalance
                                        ? String(
                                            values[openAccordianIndex]
                                              .serviceStartingBalance,
                                          )
                                        : ''
                                    }
                                    handlechange={(e) => {
                                      const filteredValue = e.replace(
                                        /\D/g,
                                        '',
                                      );
                                      if (filteredValue) {
                                        setFieldValue(
                                          `${openAccordianIndex}.serviceStartingBalance`,
                                          Number(filteredValue),
                                        );
                                      } else {
                                        setFieldValue(
                                          `${openAccordianIndex}.serviceStartingBalance`,
                                          '',
                                        );
                                      }
                                    }}
                                    Required
                                  />
                                  {errors[openAccordianIndex]
                                    ?.serviceStartingBalance &&
                                    touched[openAccordianIndex]
                                      ?.serviceStartingBalance && (
                                      <MediumTypography
                                        labelid={String(
                                          errors[openAccordianIndex]
                                            ?.serviceStartingBalance,
                                        )}
                                        defaultlabel="Starting Balance cannot be empty"
                                        sxProps={{
                                          paddingTop: '8px',
                                          color: 'var( --thom-radical-red)',
                                        }}
                                      />
                                    )}
                                </Grid>
                                <Grid item xs={2}></Grid>
                                <Grid item xs={5}>
                                  <Textinput
                                    className="bg__white"
                                    labelid="Waivers.serviceCountsUsed"
                                    defaultlabelid="Service Counts Used"
                                    type="number"
                                    Value={String(
                                      values[openAccordianIndex]
                                        .serviceUsedBalance === null
                                        ? ''
                                        : values[openAccordianIndex]
                                            .serviceUsedBalance,
                                    )}
                                    disabled={true}
                                  />
                                </Grid>
                                <Grid item xs={5}>
                                  <Textinput
                                    className="bg__white"
                                    labelid="Waivers.serviceCountsBalance"
                                    defaultlabelid="Service Counts Balance"
                                    type="number"
                                    Value={String(
                                      values[openAccordianIndex]
                                        .serviceRemainingBalance === null
                                        ? ''
                                        : values[openAccordianIndex]
                                            .serviceRemainingBalance,
                                    )}
                                    disabled={true}
                                  />
                                </Grid>
                                <Grid item xs={2} />
                              </>
                            )}
                          </Grid>
                          <Box className="flex__ justifyContent-End mt-md">
                            <ButtonComponent
                              className="btn-primary btn-cancel mr-sm"
                              variantType="contained"
                              defaultLabelId="Cancel"
                              labelId="Waivers.cancel"
                              sxProps={{
                                display: hasEditPermission ? 'auto' : 'none',
                              }}
                              onClick={() => {
                                if (
                                  !_.isEqual(
                                    initialBackupWaiver &&
                                      initialBackupWaiver[openAccordianIndex],
                                    initialWaiver[openAccordianIndex],
                                  )
                                ) {
                                  setShowUnsavedAlertDialog(true);
                                } else {
                                  setShowUnsavedAlertDialog(false);
                                }
                              }}
                            />
                            <ButtonComponent
                              className="btn-primary btn-submit"
                              variantType="contained"
                              type="submit"
                              labelId="Waivers.submit"
                              defaultLabelId="Submit"
                              sxProps={{
                                display: hasEditPermission ? 'auto' : 'none',
                              }}
                              onClick={() => {
                                handleSubmit();
                              }}
                            />
                          </Box>
                        </Box>
                      );
                    }}
                  </Formik>
                )}
            </Box>
          ))}
          {initialWaiver && initialWaiver?.length > 0 && (
            <MediumTypography
              labelid="Waivers.addAnotherWaiver"
              defaultlabel="+ Add Another Waiver"
              fontSize="16px"
              fontweight={700}
              textColor={
                checkPermissionForFeature(
                  'backend.waiver_information',
                  'addPermission',
                ) &&
                checkPermissionForFeature('backend.clients', 'editPermission')
                  ? '#00C6B8'
                  : '#97A6A5'
              }
              sxProps={{
                cursor:
                  checkPermissionForFeature(
                    'backend.waiver_information',
                    'addPermission',
                  ) &&
                  checkPermissionForFeature('backend.clients', 'editPermission')
                    ? 'pointer'
                    : 'not-allowed',
                visibility: hasEditPermission ? 'visible' : 'hidden',
              }}
              className="cursorPointer mt-md"
              onClick={() => {
                if (
                  checkPermissionForFeature(
                    'backend.waiver_information',
                    'addPermission',
                  ) &&
                  checkPermissionForFeature('backend.clients', 'editPermission')
                ) {
                  handleAddNewWaiver();
                }
              }}
            />
          )}
        </Card>
      )}
    </Box>
  );
};

export default WaiversScreen;
